import { Link } from 'react-router-dom';
import { useClub } from '../../app/club';
import {
  useAccessCodes,
  useAccessRules,
  useDoors,
  useGroupMembers,
  useGroups,
  useLocks,
  usePersons,
  useSyncJobs,
} from '../../data/hooks';
import { Battery, ErrorState, Loading, PageHeader, Panel, SyncBadge } from '../../components/ui';
import { doorSyncStatuses, doorsAwaitingSync, isRevokeOverdue } from '../../lib/sync';
import { alertThreshold, batteryMeasuredAt, lowBatteryLocks } from '../../lib/battery';
import { fmtAge, fmtDate, fmtDateTime, MODEL_LABEL } from '../../lib/format';
import { codeValidOnDay } from '../../lib/codes';
import { computeScope, inScope } from '../../app/scope';
import { can } from '../../lib/roles';
import { OnboardingChecklist } from '../../components/OnboardingChecklist';

export function DashboardPage() {
  const { orgId, org, role, managedGroupIds, readOnly } = useClub();
  const isManager = role === 'group_manager';
  const doors = useDoors(orgId);
  const locks = useLocks(orgId);
  const jobs = useSyncJobs(orgId);
  const codes = useAccessCodes(orgId);
  const members = useGroupMembers(orgId);
  const groups = useGroups(orgId);
  const persons = usePersons(orgId);
  const rules = useAccessRules(orgId);

  const all = [doors, locks, jobs, codes, members, groups, persons, rules];
  if (all.some((q) => q.isLoading)) return <Loading />;
  const err = all.find((q) => q.error)?.error;
  if (err) return <ErrorState error={err} />;

  const scope = computeScope(role, managedGroupIds, members.data!, rules.data!);
  const statuses = doorSyncStatuses(doors.data!, locks.data!, jobs.data!);
  const awaiting = doorsAwaitingSync(statuses);
  const overdue = awaiting.filter((s) => isRevokeOverdue(s, org.revoke_alert_hours));
  const batteryThreshold = alertThreshold(org);
  const lowLockIds = new Set(lowBatteryLocks(locks.data!, batteryThreshold).map((l) => l.id));
  const lowBattery = statuses
    .filter((s) => s.lock && lowLockIds.has(s.lock.id))
    .sort((a, b) => (a.lock!.battery_pct ?? 0) - (b.lock!.battery_pct ?? 0));
  const today = new Date();
  const doorByLock = new Map(doors.data!.filter((d) => d.lock_id).map((d) => [d.lock_id!, d]));
  const codesToday = codes.data!.filter(
    (c) => codeValidOnDay(c, today) && inScope(scope, 'doorIds', doorByLock.get(c.lock_id)?.id ?? ''),
  );
  const horizon = today.getTime() + 30 * 86400_000;
  const personById = new Map(persons.data!.map((p) => [p.id, p]));
  const groupById = new Map(groups.data!.map((g) => [g.id, g]));
  const expiring = members
    .data!.filter((m) => m.valid_until && new Date(m.valid_until).getTime() <= horizon && new Date(m.valid_until) > today)
    .filter((m) => inScope(scope, 'groupIds', m.group_id))
    .sort((a, b) => a.valid_until!.localeCompare(b.valid_until!));

  return (
    <div className="stack-lg">
      <PageHeader title="Dashboard" intro={`Wat er vandaag bij ${org.name} aandacht vraagt.`} />

      <div className="quick-actions">
        {can('code.create', role) && !readOnly && (
          <Link className="btn btn-primary" to={`/c/${orgId}/codes?nieuw=scheidsrechter`}>
            Code voor scheidsrechter
          </Link>
        )}
        {can('person.create', role) && !readOnly && (
          <Link className="btn btn-secondary" to={`/c/${orgId}/personen?nieuw=1`}>
            Persoon toevoegen
          </Link>
        )}
      </div>

      {(role === 'platform_admin' || role === 'club_admin') && <OnboardingChecklist orgId={orgId} />}

      {!isManager && overdue.length > 0 && (
        <div className="alert alert-danger" role="alert">
          <strong>Intrekking staat langer dan {org.revoke_alert_hours} uur open.</strong> Loop met de app langs{' '}
          {overdue.map((s, i) => (
            <span key={s.door.id}>
              {i > 0 && (i === overdue.length - 1 ? ' en ' : ', ')}
              <Link to={`/c/${orgId}/deuren?deur=${s.door.id}`}>{s.door.name}</Link>
            </span>
          ))}
          . Tot dan werkt de ingetrokken pas of toegang nog op die deur.
        </div>
      )}

      <div className="dash-grid">
        {!isManager && (
          <Panel title="Wacht op sync" description="Oudste eerst. Wijzigingen gaan naar het slot bij de volgende app-verbinding.">
            {awaiting.length === 0 ? (
              <p className="tile-empty">Alle deuren zijn bijgewerkt.</p>
            ) : (
              <ul className="tile-list">
                {awaiting.map((s) => (
                  <li key={s.door.id}>
                    <span>
                      <Link className="primary" to={`/c/${orgId}/deuren?deur=${s.door.id}`}>
                        {s.door.name}
                      </Link>
                      <span className="cell-sub">open sinds {fmtAge(s.oldestPendingAt)}</span>
                    </span>
                    <SyncBadge state={s.oldestRevokeAt ? 'revoke' : 'pending'} count={s.pendingCount} age={fmtAge(s.oldestPendingAt)} />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        )}

        {!isManager && (
          <Panel
            title="Batterijen"
            description={`Sloten op of onder ${batteryThreshold}%. Clubbeheerders krijgen hierover ook een e-mail.`}
          >
            {lowBattery.length === 0 ? (
              <p className="tile-empty">Alle batterijen zijn in orde.</p>
            ) : (
              <ul className="tile-list">
                {lowBattery.map((s) => (
                  <li key={s.door.id}>
                    <span>
                      <Link className="primary" to={`/c/${orgId}/deuren?deur=${s.door.id}`}>
                        {s.door.name}
                      </Link>
                      <span className="cell-sub">{MODEL_LABEL[s.lock!.model]}</span>
                    </span>
                    <span style={{ textAlign: 'right' }}>
                      <Battery pct={s.lock!.battery_pct} measuredAt={batteryMeasuredAt(s.lock!)} detail />
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        )}

        <Panel title="Codes die vandaag gelden" description={`Tijdelijke codes op deuren met codeklavier (${MODEL_LABEL.D1_K}).`}>
          {codesToday.length === 0 ? (
            <p className="tile-empty">Vandaag geldt geen tijdelijke code.</p>
          ) : (
            <ul className="tile-list">
              {codesToday.map((c) => (
                <li key={c.id}>
                  <span>
                    <span className="primary">{c.label}</span>
                    <span className="cell-sub">
                      {doorByLock.get(c.lock_id)?.name ?? 'Onbekende deur'}, {fmtDateTime(c.valid_from)} tot {fmtDateTime(c.valid_until)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Toegang die binnenkort stopt" description="Groepsleden met een einddatum in de komende 30 dagen.">
          {expiring.length === 0 ? (
            <p className="tile-empty">Niemand verliest de komende 30 dagen toegang.</p>
          ) : (
            <ul className="tile-list">
              {expiring.map((m) => (
                <li key={`${m.group_id}:${m.person_id}`}>
                  <span>
                    <Link className="primary" to={`/c/${orgId}/personen/${m.person_id}`}>
                      {personById.get(m.person_id)?.full_name ?? 'Onbekend'}
                    </Link>
                    <span className="cell-sub">{groupById.get(m.group_id)?.name}</span>
                  </span>
                  <span className="badge badge-yellow">tot {fmtDate(m.valid_until)}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
